// Textos y opciones de Inventario v3, copiados del mock «Portal Admin VL v3».
import type { InventoryLensPeriod } from '@/api/services/retail-catalog/backoffice';
import { reportPeriodLabel } from '@/shared/patterns/PeriodBand';
import {
    PRODUCT_KIND_LABEL,
    PRODUCT_KIND_OPTIONS,
    type ProductKind,
} from '@/shared/patterns/KindChip';
import type { SortColumn, StockBucket, TierKey } from './inventoryList';

/** Chips de tipo en el orden del mock; Kit y Servicio van tras Accesorio. */
export const KIND_OPTIONS: readonly { value: ProductKind; label: string }[] = PRODUCT_KIND_OPTIONS;

/** Chips de gama, en el orden del mock. */
export const TIER_OPTIONS: readonly { value: TierKey; label: string }[] = [
    { value: 'HIGH', label: 'Alta' },
    { value: 'MID', label: 'Media' },
    { value: 'LOW', label: 'Baja' },
    { value: 'NONE', label: 'Sin gama' },
];

export const KIND_LABEL: Record<ProductKind, string> = PRODUCT_KIND_LABEL;

export type BucketTone = 'neutral' | 'success' | 'danger' | 'muted' | 'warning';

/** Tarjetas de conteo, en el orden del mock, con su color y su ayuda. */
export const BUCKET_OPTIONS: readonly {
    value: StockBucket;
    label: string;
    tone: BucketTone;
    hint?: string;
}[] = [
    { value: 'ALL', label: 'Todos', tone: 'neutral' },
    { value: 'IN_STOCK', label: 'Con stock', tone: 'success', hint: 'SKUs con stock > 0' },
    {
        value: 'LOW_STOCK',
        label: 'Stock bajo',
        tone: 'danger',
        hint: 'Días de cobertura < 7 con stock > 0',
    },
    { value: 'OUT_OF_STOCK', label: 'Sin stock', tone: 'muted', hint: 'Stock 0 en el tenant' },
    {
        value: 'SLOW_MOVER',
        label: 'Lento',
        tone: 'warning',
        hint: 'Stock > 5 y 0 ventas en 30d',
    },
];

/** Columnas de la tabla, todas ordenables; los anchos suman el 100% y caben en ~700px sin cortar cifras. */
export interface InventoryColumn {
    column: SortColumn;
    label: string;
    /** Nombre completo de una etiqueta corta, para el `title` del encabezado. */
    note?: string;
    numeric: boolean;
    width: string;
}

/** Columnas de la tabla; la de ventas toma su encabezado del periodo con `columnsForPeriod`. */
export const COLUMNS: readonly InventoryColumn[] = [
    { column: 'name', label: 'Nombre', numeric: false, width: '19%' },
    { column: 'sku', label: 'SKU', numeric: false, width: '15%' },
    { column: 'kind', label: 'Clasif.', note: 'Clasificación', numeric: false, width: '12%' },
    { column: 'stock', label: 'Stock', numeric: true, width: '9%' },
    {
        column: 'sales',
        label: 'Vtas',
        note: 'Ventas del periodo',
        numeric: true,
        width: '11.5%',
    },
    { column: 'coverage', label: 'Cob.', note: 'Cobertura en días', numeric: true, width: '8%' },
    { column: 'price', label: 'Precio', numeric: true, width: '12.5%' },
    { column: 'alerts', label: 'Alertas', numeric: false, width: '13%' },
];

/** Las columnas con el encabezado de ventas del periodo de la banda (`Vtas 7d`, `Vtas todo`). */
export function columnsForPeriod(period: InventoryLensPeriod): InventoryColumn[] {
    return COLUMNS.map((c) =>
        c.column === 'sales'
            ? {
                  ...c,
                  label: period === 'all' ? 'Vtas todo' : `Vtas ${period}`,
                  note: `Ventas · ${reportPeriodLabel(period).toLowerCase()}`,
              }
            : c,
    );
}

/** Dato que no aplica a la fila de tarjetas PCF (ventas, cobertura, precio, alertas). */
export const NOT_APPLICABLE = 'N/A';

/** Formato entero es-MX del mock (`fmtInt`). */
export function formatInt(n: number): string {
    return n.toLocaleString('es-MX');
}

/** «N SKUs» / «1 SKU». */
export function skuCountLabel(n: number): string {
    return `${formatInt(n)} SKU${n === 1 ? '' : 's'}`;
}
